/**
 * /guides/how-to-create-wedding-budget
 *
 * "How to Create a Wedding Budget: A Practical Guide" — the eleventh guide, and
 * the third side of the planning triangle the ninth and tenth started. The
 * checklist guide owns WHEN the work happens; the vendor guide owns each
 * commitment as its own record; this one owns the money the whole plan is spent
 * against.
 *
 * ── The differentiator, which is the whole piece ──────────────────────────
 * The search results for this query are four formats and one article: a generic
 * "how to budget", a percentage split, a downloadable spreadsheet, and a market
 * cost report. None of them is a system — they all stop at allocation, which is
 * the easy half. What a couple actually cannot do, six months in, is answer "how
 * much have we got left?" without getting two different numbers depending on
 * which one they subtract.
 *
 * So the article is built on one chain, stated once as an ordered list in the
 * first section and then walked section by section:
 *
 *   available money -> budget -> categories -> estimates -> quotes ->
 *   committed -> paid -> remaining -> uncommitted -> the next decision
 *
 * And on five terms kept strictly apart, because conflating any two is the
 * defect this guide exists to fix:
 *
 *   - ESTIMATED   what you currently think it may cost
 *   - QUOTED      what a vendor currently says it will cost
 *   - COMMITTED   what you have actually agreed to
 *   - PAID        what has actually left your account
 *   - REMAINING   committed minus paid, which is money already owed
 *
 * The load-bearing sentence of the whole guide is in "Committed, Paid and What
 * Is Actually Left": total budget minus paid is NOT what you have left, because
 * it treats money you already owe as money you can still spend. Everything
 * before that section builds the numbers it needs; everything after it uses
 * them. An edit that softens that distinction, or that lets a quote read as a
 * commitment, removes the reason this article exists.
 *
 * ── Length, and the five merges that bought it ────────────────────────────
 * The commission asked for eighteen sections against a house range of roughly
 * 1,850-2,950 rendered words. It is thirteen here, and nothing was dropped:
 * five pairs were MERGED because each pair was one subject written twice.
 *
 *   1. "Start With the Total You Can Actually Spend" absorbed "Separate
 *      Available Money From Expected Contributions" — the second was a
 *      qualification of one item in the first's own list. It is now three
 *      subheadings inside one act: establishing the ceiling honestly.
 *   2. "Use Estimates Before You Have Quotes" and "Replace Estimates With Real
 *      Vendor Costs" became "Estimates First, Then Real Numbers". They are two
 *      halves of one progression, and the progression is the point; split in
 *      two it gets stated twice and understood once.
 *   3. "Account for Guest-Dependent Costs" absorbed "Use Your Guest List to
 *      Test the Budget" — the second is the thought experiment that
 *      demonstrates the first. Both linked to the same guide, which is the tell.
 *   4. "Review the Budget When Something Changes" absorbed "A Practical Budget
 *      Review Routine": one section, two triggers (an event, and a rhythm), two
 *      subheadings. Their content overlapped on updating changed quotes.
 *   5. "What a Simple Wedding Budget Should Show" absorbed "Spreadsheet,
 *      Planning Tool or Both?" — the medium question is answered by the field
 *      list it followed, so it is that section's closing paragraph rather than
 *      an H2 of its own.
 *
 * A first draft of the merged thirteen still ran to 3,897 words and came down
 * to roughly 3,250 over three passes that tightened prose without removing
 * subjects: every mandated list, hedge, definition and link survived. That is
 * still about 10% above the longest sibling (the vendor guide, 2,941), and it
 * is a deliberate call — this commission carries five defined terms, a worked
 * arithmetic example, a fifteen-item category list, a ten-field record and a
 * nine-item cost list, and cutting further would have meant dropping one of
 * them rather than tightening it. Anything added here should replace something
 * rather than extend it.
 *
 * There is ONE worked example in the article, not two: 30,000 / 24,000 / 8,000
 * carries the core section, and the photography row in the field list is the
 * same wedding seen one line at a time. A second set of numbers would be a
 * second thing to hold, for nothing.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 * This is the guide where a careless sentence becomes financial advice or an
 * invented market statistic. The rules it is written under:
 *
 *   - NO wedding costs anything. No average, no national figure, no "expect to
 *     pay". The article never estimates a market price, because that claim
 *     would need a source, a market and a year, and none of them would still be
 *     true next year.
 *   - NO percentage split is presented as a rule. Percentages appear twice —
 *     once for the buffer, once for allocation — both times named as a common
 *     starting point that varies with guest count, location, venue, vendor
 *     pricing, what a package includes and what the couple cares about.
 *   - The 30,000 example is labelled an illustration in a note, in the section
 *     it appears in, and is never called typical or recommended.
 *   - No jurisdiction-specific tax claim. Taxes and service charges appear only
 *     as things that may or may not be inside a quoted price, which is true
 *     everywhere.
 *   - Not financial advice, and a note says so. The guide describes a way to
 *     keep track of money; it does not tell anyone what to spend, or how to
 *     find it.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * All in the closing section, all true of the shipped product:
 *
 *   - "Agapaé's Planning Hub and Éros are part of the Heirloom plan."
 *     True at `api/constants/planFeatures.mjs`: both hang off
 *     `invitationProduct`, which is `pro` (Heirloom) only.
 *   - a total budget can be set, and the numbers beside it are the ones this
 *     article teaches  (`computeOverallBudget` in
 *     `api/utils/organizerCompute.mjs` returns totalBudget, estimatedCost,
 *     paid, outstanding = cost - paid, and remaining = budget - cost; the
 *     labels are `organizer.budget.*` in
 *     `app/src/locales/langs/en/organizer.json`)
 *   - the split between "outstanding" and "remaining" is the product's own, and
 *     is exactly the distinction the core section draws
 *   - costs carry a label, an amount, a due date, a planning category and the
 *     vendor they belong to, and are either due or paid  (OrganizerPaymentSchema)
 *   - a breakdown by category  (`by_category`; there is NO per-category budget
 *     cap in the model, so none is claimed)
 *   - recorded costs above the budget are flagged  (`overBudget` -> the amber
 *     `over_budget` health reason)
 *   - dated payments appear on a calendar  (`/:eventId/organizer/calendar`)
 *   - Éros reads those totals  (`get_budget_breakdown`) and records a cost or
 *     marks one paid when asked  (`create_payment` / `update_payment`)
 *
 * And the boundaries, each a real product limit rather than modesty: Éros
 * cannot set the total budget — the persona says so in
 * `api/services/assistantPrompt.mjs` and there is no tool for it — it suggests
 * and the couple decides; Agapaé is not accounting software, gives no financial
 * advice, guarantees nothing about staying inside a budget, and does not stand
 * in for contracts, invoices or a bank statement; and it sends no reminders
 * about vendor payments (the only scheduled mail in `api/services/` is the
 * gallery retention reminder, and the Stripe mails are about the couple's own
 * subscription, which is why that denial is scoped to VENDOR payments).
 *
 * ── Boundaries with the guides it links to ────────────────────────────────
 * The vendor guide owns the per-vendor record, the contract and the payment
 * schedule; this guide stops at the committed total and hands off — twice, at
 * the two points where the subject genuinely changes (a quote becoming an
 * agreement, and the tracker-versus-budget distinction). The guest-list guide
 * owns the headcount; linked once, in the section about costs that move with
 * it. The checklist owns the order of the whole plan; linked once, at the
 * close. Four internal hrefs, none used more than three times including the
 * related block, which is the cap `guides.seo.test.mjs` enforces.
 *
 * ── Structure ─────────────────────────────────────────────────────────────
 * Thirteen sections, unnumbered as in every guide since the third. `steps` (an
 * ordered list) is used only where the order is real: the chain, the estimate
 * progression, the worked arithmetic and the review routine. The "table" the
 * brief sketches is the ten-field list in "What a Simple Wedding Budget Shows"
 * plus the worked row under it — the block vocabulary in `guideModel.js` has no
 * table type, and adding one would mean changing both renderers.
 *
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const howToCreateWeddingBudgetEn = {
  slug: "how-to-create-wedding-budget",

  title: "How to Create a Wedding Budget: A Practical Guide",
  metaTitle: "How to Create a Wedding Budget: A Practical Guide | Agapaé",
  metaDescription:
    "Learn how to create a wedding budget, set spending priorities, track estimated and committed costs, record payments, and manage what remains.",

  eyebrow: "Wedding planning",

  hero: {
    src: "/assets/images/invitation/invitation-1.jpg",
    // Describes the photograph, not the keyword: a high-key black and white
    // frame of a couple standing side by side, cropped from the shoulders
    // down — a tuxedo with a boutonnière, a plain square-neck gown, her hand
    // resting on his arm, a small bouquet held low. Two people standing
    // together in front of a decision is what this article is about. Nothing
    // is described here that is not in the frame.
    alt: "A high-key black and white photograph of a couple standing side by side, cropped from the shoulders down, her hand resting on the arm of his tuxedo as she holds a small bouquet of pale flowers",
    width: 6558,
    height: 3659,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Most wedding budgets begin as one number said out loud. For a while it does its job: it rules venues in and out, and makes the whole thing feel possible.`,
    `The difficulty arrives a few months later, when several vendors are booked, two deposits have gone out, and somebody asks how much is left. There are two honest answers to that, thousands apart, because money you have paid and money you have promised are not the same thing.`,
    `This guide is about the budget that can answer it: what you can spend, what you expect to spend, what you have committed to, what has been paid, what remains, and which decision that leaves you standing in front of.`,
  ],

  sections: [
    {
      heading: "A Wedding Budget Is a Decision Tool",
      content: [
        `A budget is not a list of what a wedding costs. It is what you consult before saying yes to something, and its value is in the questions it answers on the spot:`,
        {
          type: "list",
          items: [
            `Can we afford this, given everything we have already agreed to?`,
            `What would we have to give up to say yes?`,
            `How much of our money is already spoken for?`,
            `How much is genuinely still ours to allocate?`,
            `Which decision is the next one that actually costs something?`,
          ],
        },
        `A total and a list of categories cannot answer them. They need money tracked through its whole life, and that life has an order:`,
        {
          type: "steps",
          items: [
            `The money you actually have available.`,
            `The total you decide the wedding may cost.`,
            `The categories you divide that total into.`,
            `An estimate for each, made before you know anything.`,
            `A quote, once a real vendor gives you a real price.`,
            `A committed cost, once you have agreed to it.`,
            `What you have paid so far.`,
            `What remains to be paid on what you have committed.`,
            `What is left uncommitted, and the next decision it has to cover.`,
          ],
        },
        `Almost every budget that stops being useful stopped somewhere around step four. The rest of this guide is the other five.`,
      ],
    },
    {
      // Absorbed "Separate Available Money From Expected Contributions": it was
      // a qualification of one item in this section's own list, and belongs
      // inside the act of setting the ceiling rather than after it.
      heading: "Start With the Total You Can Actually Spend",
      content: [
        `A budget built from what a wedding is supposed to cost is somebody else's budget. Build yours from the money that will exist when the bills fall due. Three kinds get added together here, and they should not be:`,
        { type: "subheading", text: "Money you already have" },
        `What is set aside today. This is the only certain part of the total, and the only part it is safe to make firm commitments against early.`,
        { type: "subheading", text: "Money you expect to add" },
        `What you can realistically save before the wedding: an amount per month times the months you actually have, not a hopeful lump sum. Deposits leave in the same period.`,
        { type: "subheading", text: "Money that has been mentioned" },
        `Contributions from family run from a confirmed amount to a warm sentence at dinner. Count the first. Treat the second as a possible improvement rather than part of the plan, and if it becomes real, decide then what it buys.`,
        `Add the first two plus anything confirmed from the third: that is your ceiling, and every later decision is measured against it.`,
        {
          type: "note",
          text: `This guide describes a way to keep track of money, not what to spend or how to fund it. Decisions about savings, borrowing or family contributions are yours, and worth proper advice if they are significant.`,
        },
      ],
    },
    {
      heading: "Leave Room Before You Allocate Everything",
      content: [
        `Take something off the top before you divide anything up — not because weddings are mysteriously unpredictable, but for two ordinary reasons: costs estimated before anyone had a quote, and small purchases nobody wrote down.`,
        `A reserve absorbs those without a conversation. Without one the same money still gets spent, out of whichever category still has room, usually late and in a hurry.`,
        `How much is a judgement, not a formula. Planning advice commonly suggests holding back something in the region of five to ten per cent — a place to start rather than a finding, since a wedding with most of its costs already quoted needs less room than one still being imagined.`,
        `Keep it as its own visible line rather than slack hidden in every category. A reserve you can see is a reserve you can decide to spend.`,
      ],
    },
    {
      heading: "Choose the Categories You Will Decide With",
      content: [
        `Categories are not an accounting exercise. Their job is to make a trade-off visible: two things side by side, so money can be moved between them deliberately.`,
        `Most weddings are some subset of:`,
        {
          type: "list",
          items: [
            `the venue`,
            `catering and drinks`,
            `photography`,
            `videography`,
            `music and entertainment`,
            `flowers and decoration`,
            `attire for both partners`,
            `hair, makeup and beauty`,
            `stationery and printing`,
            `transportation`,
            `a planner, or a coordinator for the day`,
            `rentals, such as furniture, tableware or lighting`,
            `accommodation and guest logistics, where you are covering any`,
            `ceremony costs, such as the officiant, the venue or the paperwork`,
            `everything small, and the reserve`,
          ],
        },
        `No wedding needs all of them, and an empty category is a row to scroll past. Add one when there is a real decision inside it.`,
        `The opposite failure is more common: splitting so finely that every question needs three lines updated. If two things always get decided together, they are one category; if one line hides a decision you keep having, split it.`,
      ],
    },
    {
      heading: "Allocate Against Your Own Priorities",
      content: [
        `Two couples with the same total can produce completely different budgets and both be right. One puts a third of everything into food. One books the photographer first and works around it. One spends on the venue and invites forty people rather than two hundred.`,
        `A useful way in: each of you names two or three things that would genuinely diminish the day if they were cut. The overlap is where the money goes; the disagreements are the conversation worth having early, while allocations are cheap to move.`,
        `Percentage splits are on every wedding site, and they are a starting framework rather than a rule. They cannot know your guest count, your location, whether everyone has to fly, what your venue includes, or what vendors near you charge — any of which can move a category by half. Use one to break the blank page, then let your own quotes overwrite it.`,
        `Keep the arithmetic honest: categories must add up to the total, reserve included. An allocation has two halves, and naming the second — what gets less — makes it a decision rather than a wish.`,
      ],
    },
    {
      // Merged from "Use Estimates Before You Have Quotes" and "Replace
      // Estimates With Real Vendor Costs". Two halves of one progression, and
      // the progression is the point.
      heading: "Estimates First, Then Real Numbers",
      content: [
        `You have to put numbers in before anyone has given you one. That is how a budget starts. What matters is that every number says which kind it is.`,
        { type: "subheading", text: "An estimate" },
        `What you currently think something may cost — from a published range, from what a friend paid, or from a guess you will revise. It lets the arithmetic work, and is worth nothing as a promise.`,
        { type: "subheading", text: "A quote" },
        `What a specific vendor says it will cost, for a specific scope, usually for a limited time. Better information, and still not a commitment: until you accept it you owe nobody anything, and it can expire.`,
        `As research progresses, estimates get replaced by quotes and the budget sharpens without being rebuilt. Keep the estimate beside the quote: the difference is the news, and a category that keeps coming in high is telling you something about the whole plan.`,
        `When you accept a quote, something changes in kind:`,
        {
          type: "steps",
          items: [
            `Estimate: a number you chose, revisable at no cost.`,
            `Quote: a number a vendor gave you, still yours to decline.`,
            `Committed: a number you have agreed to, and now owe.`,
          ],
        },
        {
          type: "p",
          text: [
            `From there the agreement, not the budget, is the source of truth: take the committed amount and the payment dates from what you signed. `,
            {
              text: "How to Keep Track of Wedding Vendors and Payments",
              href: "/guides/how-to-track-wedding-vendors-payments",
            },
            ` covers holding that per-vendor detail together.`,
          ],
        },
      ],
    },
    {
      // The core section. Everything before it builds these numbers; everything
      // after it uses them.
      heading: "Committed, Paid and What Is Actually Left",
      content: [
        `This is where a budget either works or quietly stops telling the truth. Three numbers, kept apart:`,
        { type: "subheading", text: "Committed" },
        `What you have agreed to, whether or not any of it has been paid. A signed agreement for €3,000 is a commitment of €3,000 the day you sign it, not on the day the last instalment leaves.`,
        { type: "subheading", text: "Paid" },
        `What has actually left your account. Not arranged, not promised, not about to be.`,
        { type: "subheading", text: "Remaining" },
        `Committed minus paid: money you already owe and have not handed over. The least visible number in most budgets, and the one that ruins them.`,
        `Take an illustrative wedding: a total of €30,000, signed agreements worth €24,000, and €8,000 paid.`,
        {
          type: "steps",
          items: [
            `Total budget: €30,000 — what they decided they could spend.`,
            `Committed: €24,000 — agreements already made.`,
            `Paid: €8,000 — money that has actually gone.`,
            `Remaining on those commitments: €16,000 — owed, not optional.`,
            `Uncommitted budget: €6,000 — the only part still theirs to decide with.`,
          ],
        },
        `Now the subtraction that does the damage. Total budget minus paid is €22,000, and it feels like the answer to "how much have we got left?". It is not: €16,000 of that is already owed to people they have signed with. The number that answers the question is total budget minus committed — €6,000, little more than a quarter of the figure that felt right. Confusing the two is how a budget stays comfortable until the final month.`,
        `Keep both. What remains to be paid tells you what is leaving your account and roughly when; what remains uncommitted tells you what you can still say yes to.`,
        {
          type: "note",
          text: `Those figures illustrate the arithmetic. They are not a recommended or typical wedding budget: weddings vary enormously by guest count, location, season and priorities.`,
        },
      ],
    },
    {
      // Absorbed "Use Your Guest List to Test the Budget": the thought
      // experiment demonstrates this section's point, and both linked to the
      // same guide.
      heading: "Costs That Move With the Guest Count",
      content: [
        `Some of a wedding is priced per head and some is not. Knowing which is which turns the guest list from an emotional question into a financial one.`,
        `Costs that usually move with the count: catering and drinks, place settings and rentals, invitations and postage, favours, transport you are providing, and any accommodation or guest logistics you are covering. Costs that usually do not: photography and video, music, the planner, attire, hair and makeup, and often the venue hire itself.`,
        `Usually is doing real work there. A venue may charge a flat fee up to a capacity, or set a minimum spend that does not fall when you invite fewer people. Check your own quotes rather than assuming.`,
        `Then test it both ways. "If we added twenty guests, which numbers change?" is the real price of a longer list. "If we removed twenty, which numbers actually come down?" is the harder question: contracted minimums stay, deposits are gone, and a print order already placed does not shrink.`,
        {
          type: "p",
          text: [
            `Both need a headcount you trust, updated as replies come in. `,
            {
              text: "How to Organize a Wedding Guest List",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` covers building that list and keeping it current.`,
          ],
        },
      ],
    },
    {
      heading: "The Costs That Are Easy to Miss",
      content: [
        `Budgets are rarely broken by the big lines, which get quoted, negotiated and watched. They are broken by twenty small things each too small to write down.`,
        `Things that commonly arrive after the headline price:`,
        {
          type: "list",
          items: [
            `alterations and fittings, sometimes more than one round`,
            `delivery, setup, collection and travel charges`,
            `service charges and taxes, where a quote states them separately`,
            `overtime, if the day runs past the hours you booked`,
            `extra rentals discovered while planning the layout`,
            `postage, extra printing and reprints for stationery`,
            `parking, permits or venue access fees`,
            `meals for vendors who are with you all day`,
            `gifts, accessories and the small purchases of the final fortnight`,
          ],
        },
        `That is not a universal list, and no list would be. The habit is what matters: one question asked of every quote before you accept it — what is not in this number? The inclusions, exclusions and terms usually answer it.`,
        `Give the small things a home: a miscellaneous line fed honestly is worth more than fifteen categories nobody maintains.`,
      ],
    },
    {
      heading: "The Budget and the Vendor Tracker Are Not the Same Record",
      content: [
        `These two get merged constantly, and they answer different questions.`,
        { type: "subheading", text: "The budget" },
        `How the wedding as a whole fits inside the money you have. Organised by category, read when deciding whether you can afford something.`,
        { type: "subheading", text: "The vendor tracker" },
        `What each vendor costs, what was agreed, what has been paid and what falls due next. Organised by commitment, read when working out what to deal with this week.`,
        {
          type: "p",
          text: [
            `They meet at one number: a committed cost. Every agreement you sign is a line in the tracker and a commitment in the budget, and if the two disagree, one has not been updated. `,
            {
              text: "The vendor and payment guide",
              href: "/guides/how-to-track-wedding-vendors-payments",
            },
            ` covers that side in full.`,
          ],
        },
        `The failure worth naming: a budget fed only from the bank account. It counts what has been paid, shows a comfortable gap, and says nothing about the €16,000 of signed agreements behind it.`,
      ],
    },
    {
      // Absorbed "Spreadsheet, Planning Tool or Both?" — the medium question is
      // answered by the field list it followed, so it closes this section
      // rather than taking an H2 of its own.
      heading: "What a Simple Wedding Budget Shows",
      content: [
        `A budget carrying the chain in this guide needs about ten things per line:`,
        {
          type: "list",
          items: [
            `the category it belongs to`,
            `the vendor or item`,
            `the estimated cost`,
            `the quoted cost, once you have one`,
            `the committed cost, once you have agreed`,
            `what has been paid`,
            `what remains on it`,
            `the next payment date, where there is one`,
            `a status: estimated, quoted, committed, or fully paid`,
            `a note for anything that will not be obvious in six months`,
          ],
        },
        `One line, worked through: photography. Estimated at €2,500 before anyone was contacted. Quoted at €3,000 by the photographer you wanted. Committed at €3,000 when the agreement was signed. Paid €600. Remaining €2,400, with the next payment dated.`,
        `Read that and you know everything: it went €500 over its estimate, that €500 has to come from somewhere named, and €2,400 is still to leave your account. "Photography, €3,000" tells you almost nothing.`,
        `Underneath the lines, the four totals from earlier: committed, paid, remaining to be paid, and budget minus committed.`,
        `What holds it barely matters. A spreadsheet does this perfectly well; a planning tool keeps it beside the vendors and dates it relates to; plenty of couples use both. The qualities are the same either way: one place that is the truth rather than three that disagree, a status on every line, current numbers, and a clear view of what is coming.`,
      ],
    },
    {
      // Absorbed "A Practical Budget Review Routine" — one section, two
      // triggers: an event, and a rhythm.
      heading: "Reviewing the Budget",
      content: [
        `A budget goes stale from two directions: something changes and nobody updates it, or nothing changes and nobody looks.`,
        { type: "subheading", text: "When something changes" },
        `A revised quote, an upgraded package, a venue swapped, a date moved, twenty more guests. Each touches more than the line it lands on, so re-read four things: its category, your committed total, what remains to be paid, and what is left uncommitted. Then ask whether a priority further down the list has become unaffordable.`,
        `This is not a rebuild. Most changes move one line and one total and take five minutes, which is why it should happen when the change does.`,
        { type: "subheading", text: "A short, regular review" },
        `Between changes, a repeated pass keeps the numbers honest:`,
        {
          type: "steps",
          items: [
            `Read the committed total: everything you have agreed to today.`,
            `Mark as paid only the payments that have genuinely completed.`,
            `Look at what falls due next, and confirm the money will be there.`,
            `Update any quote or agreement that has changed since last time.`,
            `Compare what remains to be paid against what remains available.`,
            `Name the next decision that needs a trade-off, and who is making it.`,
          ],
        },
        `How often depends on how much is happening: little to review early, monthly while vendors are being booked, more often in the final weeks when balances land together.`,
      ],
    },
    {
      // The brief closes with a separate Agapaé section, which is what the
      // `related` block below already renders as an H2. Two H2s reading the
      // same words is a defect, and every earlier guide resolved it the same
      // way: this section is named for what it does, and "Where this fits in
      // Agapaé" stays with the related block.
      heading: "Keeping the Wedding Budget in One Place",
      content: [
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type every earlier guide put theirs.
          type: "note",
          text: `Agapaé's Planning Hub and Éros are part of the Heirloom plan.`,
        },
        `In Agapaé, planning is divided into the parts a wedding is made of, each holding its own tasks, vendors, costs, documents and decisions. A cost is recorded much as this guide describes one: a label, an amount, a due date, its category and the vendor it came from, either due or paid.`,
        `The budget page keeps the distinction this article is built on. Set a total, and beside it sit the cost recorded so far, what has been paid, what is still outstanding on it, and what is left of the budget once everything committed is taken off — the two answers to "what is left", visible at once, with a breakdown by category and a flag when what you have committed passes what you set. Dated payments still due appear on the planning calendar.`,
        `Éros is the planning assistant inside that space. It reads those totals, answers from them, and when you ask, makes the change: record a cost, mark a payment paid, add the task that follows. It suggests and you decide — it cannot set your budget, and it will not decide what you spend.`,
        `What it is not: accounting software, financial advice, or a guarantee of staying inside a budget. It sends no reminders about vendor payments, and it does not stand in for your contracts, invoices or your own record of what has left your account.`,
        {
          type: "p",
          text: [
            `The planning side is described on `,
            { text: "Wedding Planning", href: "/wedding-planning" },
            `. A budget is one part of a longer plan, and `,
            {
              text: "Wedding Planning Checklist: What to Organize and When",
              href: "/guides/wedding-planning-checklist",
            },
            ` covers the order the rest of it happens in.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the ten earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `How do I start a wedding budget?`,
      a: `Start with money rather than categories. Add up what you have set aside, what you can realistically save before the wedding, and any contribution someone has confirmed: that total is your ceiling. Hold back a reserve, divide the rest across the categories your wedding will really have, and fill them with estimates marked as estimates, replacing each with a quote as it arrives.`,
    },
    {
      q: `What categories should a wedding budget include?`,
      a: `Only the ones you will make decisions in. Most weddings involve some mixture of venue, catering and drinks, photography, videography, music, flowers and decoration, attire, beauty, stationery, transport, rentals, a planner, ceremony costs, guest logistics, and a line for small costs and the reserve. Leave out what will stay empty, and split a line only when it hides a decision.`,
    },
    {
      q: `Should I use percentages to divide my wedding budget?`,
      a: `Only as a way to break the blank page. A percentage split cannot know your guest count, your location, what your venue includes, what vendors near you charge, or what the two of you care about, and any of those can move a category by half. Use one as a starting framework, then let your own quotes overwrite it. No split is universally correct.`,
    },
    {
      q: `What is the difference between budgeted, committed and paid wedding costs?`,
      a: `Budgeted is what you planned to spend. Committed is what you have agreed to, usually in a signed contract, whether or not any of it has been paid. Paid is what has left your account. The gap between them is money you already owe, which is why your budget minus what you have paid overstates what is left. The number that answers that question is your budget minus everything committed.`,
    },
    {
      q: `How often should I review my wedding budget?`,
      a: `Whenever something changes, and on a light rhythm in between. A new quote, an upgraded package or a shift in guest count should be entered the day it happens, because it moves your committed total. Otherwise monthly is enough through the middle of planning, and more often in the final weeks when balances fall close together.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // The two guides that own the numbers this budget consumes: the per-vendor
    // commitments it totals, and the headcount several of its categories are
    // priced against. Both are already linked in the prose at the point the
    // subject changes; naming them here is the cluster relationship rather than
    // link volume, and stays under the cap the test enforces.
    guides: [
      "how-to-track-wedding-vendors-payments",
      "how-to-organize-wedding-guest-list",
    ],

    product: [
      {
        href: "/wedding-planning",
        label: "Wedding Planning",
        blurb:
          "See how the Planning Hub holds your budget beside the vendors, tasks, documents and decisions it pays for, with what you have committed, paid and still have left in one private space. Available on the Heirloom plan.",
      },
    ],
  },
};

export default howToCreateWeddingBudgetEn;
